/**
 * statColumns.js — Single source of truth for every stat-table column set.
 *
 * Each entry is an array of column descriptors compatible with SortableTable.jsx.
 * Renderers can pull the same constants so the displayed columns match the spec
 * exactly across Player Profile, Leaderboards, Game Centre Admin, etc.
 *
 * SH = total shot attempts (incl. blocked/missed) — DISTINCT from SOG.
 * SOG = shots on goal.
 */

import React from "react";

// --- Shared formatters --------------------------------------------------------

export const fmtSv = (v) => {
    // Hockey-format save percentage: .933 (not 93.3%). 1.000 for shutout.
    const n = Number(v) || 0;
    if (n >= 1) return "1.000";
    if (n <= 0) return ".000";
    return n.toFixed(3).replace(/^0\./, ".");
};

export const fmtSvFromCounts = (sa, sv) => {
    if (!sa || sa <= 0) return "—";
    return fmtSv(sv / sa);
};

// Parse a TOI value (number of minutes, "MM:SS", or "H:MM:SS") into seconds.
export const toiToSeconds = (v) => {
    if (v === undefined || v === null || v === "") return 0;
    if (typeof v === "number") return Math.round(v * 60);
    const s = String(v).trim();
    if (s.includes(":")) {
        const parts = s.split(":").map((x) => parseInt(x, 10) || 0);
        return parts.length === 3
            ? parts[0] * 3600 + parts[1] * 60 + parts[2]
            : parts[0] * 60 + (parts[1] || 0);
    }
    return Math.round((parseFloat(s) || 0) * 60);
};

// TOI → TOTAL "MM:SS" where minutes can exceed 59 (e.g. "534:55"). Per-game
// values like "14:24" are unchanged. We intentionally do NOT roll into hours
// (the old "8:54:55" format was hard to read at a glance).
export const fmtToi = (v) => {
    const sec = toiToSeconds(v);
    if (!sec) return (v === undefined || v === null || v === "") ? "—" : "—";
    const m = Math.floor(sec / 60);
    const ss = sec % 60;
    return `${m}:${String(ss).padStart(2, "0")}`;
};

// Average TOI per game as "MM:SS" (always under-an-hour style).
export const fmtToiAvg = (toi, gp) => {
    const sec = toiToSeconds(toi);
    if (!sec || !gp) return "—";
    const per = Math.round(sec / gp);
    const m = Math.floor(per / 60);
    const s = per % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
};

const fmt2 = (v) => (Number(v) || 0).toFixed(2);
const fmt1 = (v) => (Number(v) || 0).toFixed(1);
const intOr0 = (v) => (Number(v) || 0);
const orDash = (v) => (v === undefined || v === null || v === "" ? "—" : v);
const showPlus = (v) => {
    const n = Number(v) || 0;
    return n > 0 ? `+${n}` : `${n}`;
};

// ============================================================================
// GAME LOGS — Admin bulk table (Game Centre Admin)
// ============================================================================
// Skater: # | Skater | Pos | G | A | PIM | Shots | TOI | FO | Hits | +/-
// Goalie: # | Goalie | GA | Mins | SA | SVS | G | A | PIM
// "Shots" maps to SOG; "FO" maps to fow (faceoffs won).
//
// These describe the WHICH-COLUMNS for the bulk edit UI; rendering is custom
// in GameCentreAdmin.jsx, so we expose just the field list + label here.

export const GAMELOG_SKATER_FIELDS = [
    ["g", "G", "num"],
    ["a", "A", "num"],
    ["pim", "PIM", "num"],
    ["sog", "Shots", "num"],
    ["toi", "TOI", "text"],
    ["fow", "FO", "num"],
    ["hits", "Hits", "num"],
    ["plus_minus", "+/-", "num"],
];

export const GAMELOG_GOALIE_FIELDS = [
    ["goals_against", "GA", "num"],
    ["minutes", "Mins", "num"],
    ["shots_against", "SA", "num"],
    ["saves", "SVS", "num"],
    ["g", "G", "num"],
    ["a", "A", "num"],
    ["pim", "PIM", "num"],
];

// ============================================================================
// PLAYER PROFILE — Season Stats (read-only display)
// ============================================================================
// Skater: Season | Team | GP | G | A | PTS | +/- | PIM | FOW | FO | WF% | PPG | SHG | GWG | SOG | SOA | SO% | BLK | SH | SH%
// Used for both Regular Season and Playoffs (same shape).

export const SEASON_STATS_SKATER_COLS = (renderTeam) => [
    { key: "season", label: "Season", emph: true, sticky: true, sortable: false },
    { key: "team", label: "Team", render: renderTeam, sortable: false },
    { key: "gp", label: "GP", align: "right" },
    { key: "g", label: "G", emph: true, align: "right" },
    { key: "a", label: "A", emph: true, align: "right" },
    { key: "p", label: "PTS", emph: true, align: "right" },
    { key: "plus_minus", label: "+/-", align: "right", render: (r) => showPlus(r.plus_minus) },
    { key: "sh", label: "SH", align: "right" },
    { key: "pim", label: "PIM", align: "right" },
    { key: "fow", label: "FO", align: "right" },
    { key: "wf_pct", label: "WF%", align: "right", render: (r) => fmt1(r.wf_pct) },
    { key: "ppg", label: "PPG", align: "right" },
    { key: "shg", label: "SHG", align: "right" },
    { key: "gwg", label: "GWG", align: "right" },
    { key: "soa", label: "SOA", align: "right" },
    { key: "so_g", label: "SOG", align: "right" },
    { key: "sowg", label: "SOWG", align: "right" },
    { key: "toi", label: "TOI", align: "right", render: (r) => fmtToi(r.toi) },
    { key: "blk", label: "BLK", align: "right" },
    { key: "hits", label: "Hits", align: "right" },
];

// Goalie: Season | Team | GP | GA | Min | SVS | GAA | SV% | W | L | OT | SOL | SO | G | A | PIM
export const SEASON_STATS_GOALIE_COLS = (renderTeam) => [
    { key: "season", label: "Season", emph: true, sticky: true, sortable: false },
    { key: "team", label: "Team", render: renderTeam, sortable: false },
    { key: "gp", label: "GP", align: "right" },
    { key: "ga", label: "GA", align: "right", render: (r) => intOr0(r.ga) },
    { key: "minutes", label: "Min", align: "right", render: (r) => intOr0(r.minutes) },
    { key: "svs", label: "SVS", align: "right", render: (r) => intOr0(r.svs) },
    { key: "gaa", label: "GAA", emph: true, align: "right", render: (r) => fmt2(r.gaa) },
    { key: "sv_pct", label: "SV%", emph: true, align: "right", render: (r) => fmtSv(r.sv_pct) },
    { key: "w", label: "W", emph: true, align: "right" },
    { key: "l", label: "L", align: "right" },
    { key: "ot", label: "OT", align: "right", render: (r) => intOr0(r.ot ?? r.otl) },
    { key: "sol", label: "SOL", align: "right", render: (r) => intOr0(r.sol) },
    { key: "so", label: "SO", align: "right" },
    { key: "g_goalie", label: "G", align: "right", render: (r) => intOr0(r.g_goalie) },
    { key: "a_goalie", label: "A", align: "right", render: (r) => intOr0(r.a_goalie) },
    { key: "pim_goalie", label: "PIM", align: "right", render: (r) => intOr0(r.pim_goalie) },
];

// ============================================================================
// PLAYER PROFILE — Per-Game Stats (game log tab)
// ============================================================================
// Skater: Game | Date | G | A | PTS | +/- | SH | PIM | FO | WF% | PPG | SHG | GWG | SOG | SOA | BLK | TOI | Hits
// Goalie: Game | Date | Min | SA | GA | SVS | W | L | OT | SOL | GAA | SV% | SO | G | A | PIM
//
// "Game" col is rendered as an opponent + box link (custom render in PlayerProfile.jsx).

export const PER_GAME_SKATER_COLS = [
    { key: "game", label: "Game" },             // custom render
    { key: "date", label: "Date" },             // custom render
    { key: "g", label: "G", align: "right" },
    { key: "a", label: "A", align: "right" },
    { key: "p", label: "PTS", align: "right" },
    { key: "plus_minus", label: "+/-", align: "right", render: (r) => showPlus(r.plus_minus) },
    { key: "sh", label: "SH", align: "right" },
    { key: "pim", label: "PIM", align: "right" },
    { key: "fow", label: "FO", align: "right" },
    { key: "wf_pct", label: "WF%", align: "right", render: (r) => fmt1(r.wf_pct) },
    { key: "ppg", label: "PPG", align: "right" },
    { key: "shg", label: "SHG", align: "right" },
    { key: "gwg", label: "GWG", align: "right" },
    { key: "soa", label: "SOA", align: "right" },
    { key: "so_g", label: "SOG", align: "right" },
    { key: "sowg", label: "SOWG", align: "right" },
    { key: "toi", label: "TOI", align: "right", render: (r) => fmtToi(r.toi) },
    { key: "blk", label: "BLK", align: "right" },
    { key: "hits", label: "Hits", align: "right" },
];

export const PER_GAME_GOALIE_COLS = [
    { key: "game", label: "Game" },
    { key: "date", label: "Date" },
    { key: "minutes", label: "Min", align: "right" },
    { key: "shots_against", label: "SA", align: "right" },
    { key: "goals_against", label: "GA", align: "right" },
    { key: "saves", label: "SVS", align: "right" },
    { key: "w", label: "W", align: "right", render: (r) => (r.decision === "W" ? 1 : 0) },
    { key: "l", label: "L", align: "right", render: (r) => (r.decision === "L" ? 1 : 0) },
    { key: "ot", label: "OT", align: "right", render: (r) => (r.decision === "OT" || r.decision === "OTL" ? 1 : 0) },
    { key: "sol", label: "SOL", align: "right", render: (r) => (r.decision === "SOL" ? 1 : 0) },
    { key: "gaa", label: "GAA", align: "right", render: (r) => {
        const min = Number(r.minutes) || 0;
        if (!min) return "—";
        return fmt2((Number(r.goals_against) || 0) * 60 / min);
    } },
    { key: "sv_pct", label: "SV%", align: "right", render: (r) => fmtSvFromCounts(r.shots_against, r.saves) },
    { key: "so", label: "SO", align: "right", render: (r) => (r.shutout ? 1 : 0) },
    { key: "g", label: "G", align: "right" },
    { key: "a", label: "A", align: "right" },
    { key: "pim", label: "PIM", align: "right" },
];

// ============================================================================
// LEADERBOARDS — single view, GAME-LOG stats only (matches the per-game schema).
// Skaters: # | Name | Pos | Team | GP | G | A | PTS | +/- | SH | PIM | FO | WF% | PPG | SHG | GWG | SOG | SOA | TOI | Hits
// Goalies: # | Name | Team | GP | Min | SA | GA | SVS | W | L | OT | SOL | GAA | SV% | SO | G | A | PIM
// ============================================================================

const STAT_W = 52;
// Per-game value (value / GP), 2 decimals.
const perG = (v, gp) => (!gp ? "0.00" : ((Number(v) || 0) / gp).toFixed(2));

export const buildLeaderboardSkaterCols = (deps) => {
    const { renderRank, renderPlayer, renderTeam, category, perGame, mobile } = deps;
    const W = mobile ? 30 : STAT_W;
    const sc = (key, label, opts = {}) => {
        const { pg = true, render, ...rest } = opts;
        const base = { key, label, align: "right", width: W, ...rest };
        if (render) base.render = render;
        if (perGame && pg && !render) base.render = (r) => perG(r[key], r.gp);
        return base;
    };
    return [
        { key: "rank", label: "#", sortable: false, render: renderRank, width: mobile ? 26 : 38 },
        { key: "name", label: "Name", emph: true, sticky: true, render: renderPlayer, width: mobile ? 92 : 158 },
        { key: "position", label: "Pos", sortable: false, width: mobile ? 30 : 46 },
        { key: "team_id", label: "Team", render: renderTeam, sortable: false, width: mobile ? 40 : 64 },
        sc("gp", "GP", { pg: false }),
        sc("g", "G", { emph: category === "goals" }),
        sc("a", "A", { emph: category === "assists" }),
        sc("p", mobile ? "P" : "PTS", { emph: category === "points" || category === "rookies" || category === "all_time" }),
        sc("plus_minus", "+/-", { pg: false, render: (r) => showPlus(r.plus_minus) }),
        sc("sh", "SH"),
        sc("pim", "PIM"),
        sc("fow", "FO"),
        sc("wf_pct", "WF%", { pg: false, render: (r) => fmt1(r.wf_pct) }),
        sc("ppg", "PPG"),
        sc("shg", "SHG"),
        sc("gwg", "GWG"),
        sc("soa", "SOA", { pg: false, tooltip: "Shootout Attempts" }),
        sc("so_g", "SOG", { pg: false, tooltip: "Shootout Goals" }),
        sc("sowg", "SOWG", { pg: false, tooltip: "Shootout Winning Goals" }),
        sc("toi", "TOI", { render: perGame ? (r) => fmtToiAvg(r.toi, r.gp) : (r) => fmtToi(r.toi) }),
        sc("blk", "BLK"),
        sc("hits", "Hits"),
    ];
};

export const buildLeaderboardGoalieCols = (deps) => {
    const { renderRank, renderPlayer, renderTeam, category, perGame, mobile } = deps;
    const W = mobile ? 34 : STAT_W;
    const gc = (key, label, opts = {}) => {
        const { pg = true, emph = false, render } = opts;
        let rnd = render || ((r) => intOr0(r[key]));
        if (perGame && pg) rnd = (r) => perG(r[key], r.gp);
        return { key, label, align: "right", width: W, emph, render: rnd };
    };
    return [
        { key: "rank", label: "#", sortable: false, render: renderRank, width: mobile ? 26 : 38 },
        { key: "name", label: "Name", emph: true, sticky: true, render: renderPlayer, width: mobile ? 92 : 158 },
        { key: "team_id", label: "Team", render: renderTeam, sortable: false, width: mobile ? 40 : 64 },
        gc("gp", "GP", { pg: false, render: (r) => r.gp }),
        gc("minutes", "Min", { render: (r) => r.minutes }),
        gc("sa", "SA"),
        gc("ga", "GA"),
        gc("svs", "SVS"),
        gc("w", "W", { emph: category === "wins" }),
        gc("l", "L"),
        gc("ot", "OT", { render: (r) => intOr0(r.ot ?? r.otl) }),
        gc("sol", "SOL"),
        gc("gaa", "GAA", { pg: false, emph: category === "gaa", render: (r) => fmt2(r.gaa) }),
        gc("sv_pct", "SV%", { pg: false, emph: category === "sv_pct", render: (r) => fmtSv(r.sv_pct) }),
        gc("so", "SO", { emph: category === "shutouts" }),
        gc("g_goalie", "G", { render: (r) => intOr0(r.g_goalie) }),
        gc("a_goalie", "A", { render: (r) => intOr0(r.a_goalie) }),
        gc("pim_goalie", "PIM", { render: (r) => intOr0(r.pim_goalie) }),
    ];
};


// LEADERBOARDS — Rosterly Insights view (milestones + EV/PP/SH splits).
// Skaters only. Total or Per-Game.
export const buildLeaderboardInsightCols = (deps) => {
    const { renderRank, renderPlayer, renderTeam, perGame, mobile } = deps;
    const W = mobile ? 36 : STAT_W;
    const sc = (key, label, opts = {}) => {
        const { pg = true, render, ...rest } = opts;
        const base = { key, label, align: "right", width: W, ...rest };
        if (render) base.render = render;
        if (perGame && pg && !render) base.render = (r) => perG(r[key], r.gp);
        return base;
    };
    return [
        { key: "rank", label: "#", sortable: false, render: renderRank, width: mobile ? 26 : 38 },
        { key: "name", label: "Name", emph: true, sticky: true, render: renderPlayer, width: mobile ? 92 : 158 },
        { key: "position", label: "Pos", sortable: false, width: mobile ? 30 : 46 },
        { key: "team_id", label: "Team", render: renderTeam, sortable: false, width: mobile ? 40 : 64 },
        sc("gp", "GP", { pg: false }),
        sc("hat", "HAT", { emph: true, tooltip: "Hat tricks (3+ goals in a game)" }),
        sc("gwg", "GWG", { emph: true, tooltip: "Game-winning goals" }),
        sc("fg", "FG", { emph: true, tooltip: "First goals of the game" }),
        sc("en", "ENG", { tooltip: "Empty-net goals" }),
        sc("ppg", "PPG", { tooltip: "Power-play goals" }),
        sc("shg", "SHG", { tooltip: "Short-handed goals" }),
        sc("evg", "EVG", { tooltip: "Even-strength goals" }),
        sc("evp", "EVP", { tooltip: "Even-strength points" }),
        sc("ppp", "PPP", { tooltip: "Power-play points" }),
        sc("a1", "A1", { tooltip: "Primary assists" }),
        sc("blk", "BLK", { tooltip: "Blocked shots" }),
        sc("hits", "Hits"),
        sc("fow", "FOW", { tooltip: "Faceoff wins" }),
    ];
};


// ============================================================================
// LEADERBOARDS — COMPREHENSIVE column sets (every stat on the site incl. game box)
// Used by the configurable Leaderboards table; admins can show/hide any of these.
// ============================================================================

// [key, label, tooltip, optional formatter]
const SK_STAT_DEFS = [
    ["gp", "GP", "Games played"],
    ["g", "G", "Goals"],
    ["a", "A", "Assists"],
    ["p", "PTS", "Points"],
    ["plus_minus", "+/-", "Plus / minus", showPlus],
    ["sh", "SH", "Shot attempts (incl. blocked/missed)"],
    ["sog", "SOG", "Shots on goal"],
    ["sh_pct", "SH%", "Shooting percentage", fmt1],
    ["pim", "PIM", "Penalty minutes"],
    ["fo", "FO", "Faceoffs taken"],
    ["fow", "FOW", "Faceoff wins"],
    ["wf_pct", "WF%", "Faceoff win %", fmt1],
    ["ppg", "PPG", "Power-play goals"],
    ["ppa", "PPA", "Power-play assists"],
    ["ppp", "PPP", "Power-play points"],
    ["shg", "SHG", "Short-handed goals"],
    ["sha", "SHA", "Short-handed assists"],
    ["shp", "SHP", "Short-handed points"],
    ["evg", "EVG", "Even-strength goals"],
    ["eva", "EVA", "Even-strength assists"],
    ["evp", "EVP", "Even-strength points"],
    ["gwg", "GWG", "Game-winning goals"],
    ["otg", "OTG", "Overtime goals"],
    ["g_p1", "G1", "1st-period goals"],
    ["g_p2", "G2", "2nd-period goals"],
    ["g_p3", "G3", "3rd-period goals"],
    ["fg", "FG", "First goals of the game"],
    ["lg", "LG", "Last goals of the game"],
    ["en", "ENG", "Empty-net goals"],
    ["ua", "UA", "Unassisted goals"],
    ["hat", "HAT", "Hat tricks (3+ goals)"],
    ["a1", "A1", "Primary assists"],
    ["a2", "A2", "Secondary assists"],
    ["sowg", "SOWG", "Shootout-winning goals"],
    ["soa", "SOA", "Shootout attempts"],
    ["blk", "BLK", "Blocked shots"],
    ["hits", "Hits", "Hits"],
    ["toi", "TOI", "Time on ice", fmtToi],
];

const G_STAT_DEFS = [
    ["gp", "GP", "Games played"],
    ["minutes", "Min", "Minutes played"],
    ["w", "W", "Wins"],
    ["l", "L", "Losses"],
    ["ot", "OT", "Overtime losses"],
    ["sol", "SOL", "Shootout losses"],
    ["sa", "SA", "Shots against"],
    ["svs", "SVS", "Saves"],
    ["ga", "GA", "Goals against"],
    ["gaa", "GAA", "Goals-against average", fmt2],
    ["sv_pct", "SV%", "Save percentage", fmtSv],
    ["so", "SO", "Shutouts"],
    ["g_soa", "SOA", "Shootout attempts faced"],
    ["g_soga", "SOGA", "Shootout goals against"],
    ["g_sows", "SOWS", "Shootout winning saves"],
    ["g_goalie", "G", "Goals"],
    ["a_goalie", "A", "Assists"],
    ["pim_goalie", "PIM", "Penalty minutes"],
];

const SK_EMPH = { points: "p", goals: "g", assists: "a", rookies: "p", all_time: "p" };

export const LB_SK_STAT_KEYS = SK_STAT_DEFS.map((d) => d[0]);
export const LB_G_STAT_KEYS = G_STAT_DEFS.map((d) => d[0]);
export const LB_STAT_LABELS = {
    ...Object.fromEntries(SK_STAT_DEFS.map((d) => [d[0], { label: d[1], title: d[2] }])),
    ...Object.fromEntries(G_STAT_DEFS.map((d) => [d[0], { label: d[1], title: d[2] }])),
};

// Comprehensive skater leaderboard columns (basic AND insights share this superset).
export const buildLbSkaterAll = (deps) => {
    const { renderRank, renderPlayer, renderTeam, category, mobile } = deps;
    const W = mobile ? 42 : 58;
    const cols = [
        { key: "rank", label: "#", sortable: false, render: renderRank, width: mobile ? 30 : 46 },
        { key: "name", label: "Name", emph: true, sticky: true, render: renderPlayer, align: "left", width: mobile ? 132 : 210 },
        { key: "position", label: "Pos", sortable: false, align: "center", width: mobile ? 42 : 56 },
        { key: "team_id", label: "Team", render: renderTeam, sortable: false, align: "center", width: mobile ? 46 : 72 },
    ];
    SK_STAT_DEFS.forEach(([key, label, tooltip, fmt]) => {
        cols.push({ key, label, tooltip, align: "right", width: W, emph: SK_EMPH[category] === key, render: fmt ? (r) => fmt(r[key]) : undefined });
    });
    return cols;
};

export const buildLbGoalieAll = (deps) => {
    const { renderRank, renderPlayer, renderTeam, category, mobile } = deps;
    const W = mobile ? 46 : 60;
    const emphKey = { sv_pct: "sv_pct", gaa: "gaa", wins: "w", shutouts: "so" }[category];
    const cols = [
        { key: "rank", label: "#", sortable: false, render: renderRank, width: mobile ? 30 : 46 },
        { key: "name", label: "Name", emph: true, sticky: true, render: renderPlayer, align: "left", width: mobile ? 132 : 210 },
        { key: "team_id", label: "Team", render: renderTeam, sortable: false, align: "center", width: mobile ? 46 : 72 },
    ];
    G_STAT_DEFS.forEach(([key, label, tooltip, fmt]) => {
        cols.push({ key, label, tooltip, align: "right", width: W, emph: emphKey === key, render: fmt ? (r) => fmt(r[key]) : ((r) => intOr0(r[key])) });
    });
    return cols;
};
